import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ServicesFooter() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setEmail('');
      setTimeout(() => setIsSubmitted(false), 3000);
    }
  };

  return (
    <footer className="bg-[#1a1a2e] text-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B6B]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#4ECDC4]/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        {/* Top Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16 pb-16 border-b border-white/10">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] flex items-center justify-center">
                <i className="ri-luggage-cart-line text-white text-2xl"></i>
              </div>
              <span className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                AIR BAGGAGE<span className="text-[#FF6B6B]"> REPAIR SOLUTIONS</span>
              </span>
            </Link>
            <p className="text-white/60 text-lg mb-4 max-w-md" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.tagline')}
            </p>
            <p className="text-white/50 text-sm mb-6 max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-linkedin-fill', color: 'from-[#0077B5] to-[#00A0DC]' },
                { icon: 'ri-facebook-fill', color: 'from-[#1877F2] to-[#42B72A]' },
                { icon: 'ri-twitter-x-fill', color: 'from-[#1DA1F2] to-[#14171A]' },
                { icon: 'ri-instagram-line', color: 'from-[#F58529] to-[#DD2A7B]' }
              ].map((social, index) => (
                <a 
                  key={index}
                  href="#" 
                  className={`w-11 h-11 bg-gradient-to-br ${social.color} rounded-xl flex items-center justify-center hover:scale-110 transition-transform cursor-pointer`}
                >
                  <i className={`${social.icon} text-white`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.newsletterTitle')}
            </h3>
            <p className="text-white/60 mb-6">{t('footer.newsletterDesc')}</p>
            <form onSubmit={handleNewsletter} className="flex gap-3">
              <input 
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.newsletterPlaceholder')}
                className="flex-1 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#FF6B6B] transition-colors"
                required
              />
              <button 
                type="submit"
                className="px-6 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] rounded-xl font-semibold hover:shadow-lg hover:shadow-[#FF6B6B]/30 transition-all cursor-pointer whitespace-nowrap"
              >
                {isSubmitted ? (
                  <i className="ri-check-line text-xl"></i>
                ) : (
                  <i className="ri-send-plane-fill text-xl"></i>
                )}
              </button>
            </form>
            {isSubmitted && (
              <p className="text-[#4ECDC4] text-sm mt-3 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill"></i>
                {t('footer.newsletterSuccess')}
              </p>
            )}
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid md:grid-cols-4 gap-8 mb-16">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{t('nav.services')}</h3>
            <ul className="space-y-3">
              {['Fast-Track Aéroport', 'Gestion Sinistres', 'Logistique Premium', 'Plateforme SaaS'].map((item) => (
                <li key={item}>
                  <Link to="/services" className="text-white/70 hover:text-[#FF6B6B] transition-colors cursor-pointer">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white/40 mb-4">{t('footer.companyTitle')}</h3>
            <ul className="space-y-3">
              {[t('footer.company1'), t('footer.company2'), t('footer.company3'), t('footer.company4')].map((item) => (
                <li key={item}>
                  <a href="#" className="text-white/70 hover:text-[#FF6B6B] transition-colors">{item}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-white/40 mb-4">Conformité</h3>
            <ul className="space-y-3">
              {['Standards IATA', 'Normes ONDA', 'Réglementations EU', 'Documentation'].map((item) => (
                <li key={item}>
                  <a href="#" className="text-white/70 hover:text-[#FF6B6B] transition-colors">{item}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="text-base font-bold text-white mb-5"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('footer.contactTitle')}
            </h4>
            <div className="space-y-3 text-sm text-white/70">
              <div>
                <p className="font-medium text-white/90 mb-2">🇧🇪 {t('footer.contactBelgium')}</p>
                <a href="tel:+3210398383" className="hover:text-white transition-colors">
                  +32 10 39 83 83
                </a>
              </div>
              <div>
                <p className="font-medium text-white/90 mb-2">🇨🇭 Suisse</p>
                <a href="tel:+41225624248" className="hover:text-white transition-colors">
                  +41 22 562 42 48
                </a>
              </div>
              <div>
                <p className="font-medium text-white/90 mb-2">🇲🇦 {t('footer.contactMorocco')}</p>
                <a href="tel:+212531188466" className="hover:text-white transition-colors">
                  +212 5 31 18 84 66
                </a>
              </div>
              <div>
                <p className="font-medium text-white/90 mb-2">🇺🇸 USA</p>
                <a href="tel:+18322950203" className="hover:text-white transition-colors">
                  +1 832 295 0203
                </a>
              </div>
              <div>
                <p className="font-medium text-white/90 mb-2">🇦🇪 UAE</p>
                <a href="tel:+971585146914" className="hover:text-white transition-colors">
                  +971 58 514 6914
                </a>
              </div>
              <div className="pt-3 border-t border-white/10">
                <p className="font-medium text-white/90 mb-2">{t('footer.contactEmailBelgium')}</p>
                <a
                  href="mailto:Belgique@airbrs.com"
                  className="hover:text-white transition-colors"
                >
                  Belgique@airbrs.com
                </a>
              </div>
              <div>
                <p className="font-medium text-white/90 mb-2">{t('footer.contactEmailMorocco')}</p>
                <a
                  href="mailto:Maroc@airbrs.com"
                  className="hover:text-white transition-colors"
                >
                  Maroc@airbrs.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-white/40 text-xs">
            &copy; 2025 Air Baggage Repair Solutions. {t('footer.copyright')}
          </p>

          <div className="flex items-center gap-6 text-sm text-white/40">
            <a href="#" className="hover:text-white transition-colors">{t('footer.privacy')}</a>
            <a href="#" className="hover:text-white transition-colors">{t('footer.terms')}</a>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
              {t('footer.poweredBy')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
